import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from '../store';
import { updateError } from './user';

const BASE_URL = '/api/v1/group-dashboard/groups/';

const initialState = {
    isLoading: false,
    isMutating: false,
    error: null,
    groups: [],
    activeGroupId: null,
    period: 'this_month',
    overview: null,
    funnel: null,
    staff: null,
    marketing: null,
    treatments: null,
    leadSources: null,
    // members for manage dialog
    members: [],
    membersLoading: false,
};

const slice = createSlice({
    name: 'groupDashboard',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        setGroups(state, action) {
            state.groups = action.payload;
        },
        setActiveGroupId(state, action) {
            state.activeGroupId = action.payload;
            // Clear data when switching groups
            state.overview = null;
            state.funnel = null;
            state.staff = null;
            state.marketing = null;
            state.treatments = null;
            state.leadSources = null;
        },
        setPeriod(state, action) {
            state.period = action.payload;
        },
        setOverview(state, action) {
            state.overview = action.payload;
        },
        setFunnel(state, action) {
            state.funnel = action.payload;
        },
        setStaff(state, action) {
            state.staff = action.payload;
        },
        setMarketing(state, action) {
            state.marketing = action.payload;
        },
        setTreatments(state, action) {
            state.treatments = action.payload;
        },
        setLeadSources(state, action) {
            state.leadSources = action.payload;
        },
        setMutating(state, action) {
            state.isMutating = action.payload;
        },
        setMembers(state, action) {
            state.members = action.payload;
        },
        setMembersLoading(state, action) {
            state.membersLoading = action.payload;
        },
        upsertGroup(state, action) {
            const idx = state.groups.findIndex((g) => g.id === action.payload.id);
            if (idx >= 0) {
                state.groups[idx] = action.payload;
            } else {
                state.groups.push(action.payload);
            }
        },
        removeGroup(state, action) {
            state.groups = state.groups.filter((g) => g.id !== action.payload);
        },
        reset() {
            return initialState;
        },
    },
});

export const { reset: resetGroupDashboard, setActiveGroupId, setPeriod } = slice.actions;

export default slice.reducer;

// --------------- Thunks ---------------

export function fetchGroups() {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(BASE_URL);
            if (response.status === 200) {
                const groups = response.data?.results || response.data || [];
                dispatch(slice.actions.setGroups(groups));
                // Auto-select first group if none selected
                if (groups.length > 0) {
                    dispatch(slice.actions.setActiveGroupId(groups[0].id));
                }
            } else {
                dispatch(slice.actions.setError(response.data?.detail || 'Failed to fetch groups'));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to fetch groups' }));
            dispatch(slice.actions.setError(err.message || 'Failed to fetch groups'));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchGroupOverview(groupId, period = 'this_month') {
    return async () => {
        try {
            const response = await callApi(`${BASE_URL}${groupId}/overview/?period=${period}`);
            if (response.status === 200) {
                dispatch(slice.actions.setOverview(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch group overview:', err);
        }
    };
}

export function fetchGroupFunnel(groupId, period = 'this_month') {
    return async () => {
        try {
            const response = await callApi(`${BASE_URL}${groupId}/funnel/?period=${period}`);
            if (response.status === 200) {
                dispatch(slice.actions.setFunnel(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch group funnel:', err);
        }
    };
}

export function fetchGroupStaff(groupId, period = 'this_month') {
    return async () => {
        try {
            const response = await callApi(`${BASE_URL}${groupId}/staff/?period=${period}`);
            if (response.status === 200) {
                dispatch(slice.actions.setStaff(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch group staff:', err);
        }
    };
}

export function fetchGroupMarketing(groupId, period = 'this_month') {
    return async () => {
        try {
            const response = await callApi(`${BASE_URL}${groupId}/marketing/?period=${period}`);
            if (response.status === 200) {
                dispatch(slice.actions.setMarketing(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch group marketing:', err);
        }
    };
}

export function fetchGroupTreatments(groupId, period = 'this_month') {
    return async () => {
        try {
            const response = await callApi(`${BASE_URL}${groupId}/treatments/?period=${period}`);
            if (response.status === 200) {
                dispatch(slice.actions.setTreatments(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch group treatments:', err);
        }
    };
}

export function fetchGroupLeadSources(groupId, period = 'this_month') {
    return async () => {
        try {
            const response = await callApi(`${BASE_URL}${groupId}/lead-sources/?period=${period}`);
            if (response.status === 200) {
                dispatch(slice.actions.setLeadSources(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch group lead sources:', err);
        }
    };
}

export function fetchAllGroupData(groupId, period = 'this_month') {
    return async () => {
        dispatch(slice.actions.setLoading(true));
        try {
            await Promise.all([
                callApi(`${BASE_URL}${groupId}/overview/?period=${period}`).then((r) => {
                    if (r.status === 200) dispatch(slice.actions.setOverview(r.data));
                }),
                callApi(`${BASE_URL}${groupId}/funnel/?period=${period}`).then((r) => {
                    if (r.status === 200) dispatch(slice.actions.setFunnel(r.data));
                }),
                callApi(`${BASE_URL}${groupId}/staff/?period=${period}`).then((r) => {
                    if (r.status === 200) dispatch(slice.actions.setStaff(r.data));
                }),
                callApi(`${BASE_URL}${groupId}/marketing/?period=${period}`).then((r) => {
                    if (r.status === 200) dispatch(slice.actions.setMarketing(r.data));
                }),
                callApi(`${BASE_URL}${groupId}/treatments/?period=${period}`).then((r) => {
                    if (r.status === 200) dispatch(slice.actions.setTreatments(r.data));
                }),
                callApi(`${BASE_URL}${groupId}/lead-sources/?period=${period}`).then((r) => {
                    if (r.status === 200) dispatch(slice.actions.setLeadSources(r.data));
                }),
            ]);
        } catch (err) {
            dispatch(slice.actions.setError(err.message || 'Failed to fetch dashboard data'));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

// --------------- Group CRUD ---------------

export function createGroup(data) {
    return async () => {
        dispatch(slice.actions.setMutating(true));
        try {
            const response = await callApi(BASE_URL, 'POST', data);
            if (response.status === 201) {
                dispatch(slice.actions.upsertGroup(response.data));
                dispatch(updateError({ success: true, message: 'Group created successfully' }));
                return { ok: true, data: response.data };
            }
            const msg = response.data?.detail || response.data?.name?.[0] || 'Failed to create group';
            dispatch(updateError({ success: false, message: msg }));
            return { ok: false };
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to create group' }));
            return { ok: false };
        } finally {
            dispatch(slice.actions.setMutating(false));
        }
    };
}

export function updateGroup(groupId, data) {
    return async () => {
        dispatch(slice.actions.setMutating(true));
        try {
            const response = await callApi(`${BASE_URL}${groupId}/`, 'PATCH', data);
            if (response.status === 200) {
                dispatch(slice.actions.upsertGroup(response.data));
                dispatch(updateError({ success: true, message: 'Group updated successfully' }));
                return { ok: true, data: response.data };
            }
            const msg = response.data?.detail || 'Failed to update group';
            dispatch(updateError({ success: false, message: msg }));
            return { ok: false };
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to update group' }));
            return { ok: false };
        } finally {
            dispatch(slice.actions.setMutating(false));
        }
    };
}

export function deleteGroup(groupId) {
    return async () => {
        dispatch(slice.actions.setMutating(true));
        try {
            const response = await callApi(`${BASE_URL}${groupId}/`, 'DELETE');
            if (response.status === 204) {
                dispatch(slice.actions.removeGroup(groupId));
                dispatch(updateError({ success: true, message: 'Group deleted' }));
                return { ok: true };
            }
            dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to delete group' }));
            return { ok: false };
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to delete group' }));
            return { ok: false };
        } finally {
            dispatch(slice.actions.setMutating(false));
        }
    };
}

// --------------- Members CRUD ---------------

export function fetchGroupMembers(groupId) {
    return async () => {
        dispatch(slice.actions.setMembersLoading(true));
        try {
            const response = await callApi(`${BASE_URL}${groupId}/members/`);
            if (response.status === 200) {
                dispatch(slice.actions.setMembers(response.data?.results || response.data || []));
            }
        } catch (err) {
            console.error('Failed to fetch members:', err);
        } finally {
            dispatch(slice.actions.setMembersLoading(false));
        }
    };
}

export function addGroupMember(groupId, data) {
    return async () => {
        dispatch(slice.actions.setMutating(true));
        try {
            const response = await callApi(`${BASE_URL}${groupId}/members/`, 'POST', data);
            if (response.status === 201) {
                dispatch(fetchGroupMembers(groupId));
                dispatch(updateError({ success: true, message: 'Member added' }));
                return { ok: true };
            }
            dispatch(updateError({ success: false, message: response.data?.detail || response.data?.user?.[0] || 'Failed to add member' }));
            return { ok: false };
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to add member' }));
            return { ok: false };
        } finally {
            dispatch(slice.actions.setMutating(false));
        }
    };
}

export function updateGroupMember(groupId, memberId, data) {
    return async () => {
        dispatch(slice.actions.setMutating(true));
        try {
            const response = await callApi(`${BASE_URL}${groupId}/members/${memberId}/`, 'PATCH', data);
            if (response.status === 200) {
                dispatch(fetchGroupMembers(groupId));
                dispatch(updateError({ success: true, message: 'Member role updated' }));
                return { ok: true };
            }
            dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to update member' }));
            return { ok: false };
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to update member' }));
            return { ok: false };
        } finally {
            dispatch(slice.actions.setMutating(false));
        }
    };
}

export function removeGroupMember(groupId, memberId) {
    return async () => {
        dispatch(slice.actions.setMutating(true));
        try {
            const response = await callApi(`${BASE_URL}${groupId}/members/${memberId}/`, 'DELETE');
            if (response.status === 204) {
                dispatch(fetchGroupMembers(groupId));
                dispatch(updateError({ success: true, message: 'Member removed' }));
                return { ok: true };
            }
            dispatch(updateError({ success: false, message: response.data?.detail || 'Failed to remove member' }));
            return { ok: false };
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Failed to remove member' }));
            return { ok: false };
        } finally {
            dispatch(slice.actions.setMutating(false));
        }
    };
}
